// src/Api.comments.ts

import axios from "axios";

const API_DOMAIN = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN;

/**
 * Fetches comments associated with a specific evidence.
 * @param evidenceId - The ID of the evidence.
 * @param auth0 - The Auth0 instance for authentication.
 * @returns A promise resolving to the list of comments.
 */
export const listComments = async (
  evidenceId: string,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/comments?evidenceId=${encodeURIComponent(evidenceId)}`;
    const response = await axios.get(url);
    return response.data.comments;
  } catch (e: any) {
    console.error("Error fetching comments:", e);
    throw e;
  }
};

/**
 * Creates a new comment for a specific evidence.
 * @param evidenceId - The ID of the evidence.
 * @param comment - The text content of the comment.
 * @param usersMentioned - An array of users mentioned in the comment.
 * @param auth0 - The Auth0 instance for authentication.
 * @returns A promise resolving to the newly created comment.
 */
export const createComment = async (
  evidenceId: string,
  comment: string,
  usersMentioned: { _id: string}[],
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/comments?evidenceId=${encodeURIComponent(evidenceId)}`;
    const payload = {
      comment: {
        text: comment,
        usersMentioned,
      },
    };
    const response = await axios.post(url, payload);
    return response.data;
  } catch (e: any) {
    console.error("Error creating comment:", e);
    throw e;
  }
};

/**
 * Updates an existing comment's content.
 * @param evidenceId - The ID of the evidence.
 * @param commentId - The ID of the comment to update.
 * @param comment - The updated text content of the comment.
 * @param usersMentioned - An array of users mentioned in the updated comment.
 * @param auth0 - The Auth0 instance for authentication.
 * @returns A promise resolving to the updated comment.
 */
export const updateComment = async (
  commentId: string,
  comment: string,
  usersMentioned: { _id: string }[],
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/comments/${encodeURIComponent(
      commentId
    )}`;
    const payload = {
      comment: {
        text: comment,
        usersMentioned,
      },
    };
    const response = await axios.put(url, payload);
    return response.data;
  } catch (e: any) {
    console.error("Error updating comment:", e);
    throw e;
  }
};

/**
 * Deletes an existing comment.
 * @param evidenceId - The ID of the evidence.
 * @param commentId - The ID of the comment to delete.
 * @param auth0 - The Auth0 instance for authentication.
 * @returns A promise resolving to the deletion status.
 */
export const deleteComment = async (
  evidenceId: string,
  commentId: string,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/comments/${encodeURIComponent(
      commentId
    )}?evidenceId=${encodeURIComponent(evidenceId)}`;
    const response = await axios.delete(url);
    return response.data;
  } catch (e: any) {
    console.error("Error deleting comment:", e);
    throw e;
  }
};